import { BookOpenText, Lightbulb } from "lucide-react";
import { useCallback, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useApp } from "@/lib/AppContext";
import { ApiError, type SearchAnswer, type SearchResult } from "@/lib/api";
import { cn } from "@/lib/cn";
import { formatDate } from "@/lib/format";
import {
  DATE_PRESETS,
  daysBack,
  isNarrowed,
  SEARCH_MODE_LABEL,
  SEARCH_SCOPES,
  searchRequest,
  UNFILTERED,
  type SearchFilters,
} from "@/lib/searchFilters";
import { subjectKindMeta } from "@/lib/subjectKinds";

/**
 * SRCH-1/2/3 on a phone — the same search, over the same two altitudes, with the same
 * filters applied in the same place: the Server's `WHERE` clause, before ranking. Narrowing
 * a list this screen already has would be a screen that says "no results" for a query with
 * hundreds of them, and that is as true at 390 px as at 1440.
 *
 * The desktop's popovers become a horizontally scrolling strip of chips and one panel
 * beneath them, which is the handoff's shape. The date filter loses its custom range — two
 * date inputs on a phone keyboard is a form, not a filter — and keeps the five presets that
 * answer "recently" without one.
 */
export function PhoneSearch() {
  const { api, people, entities, settings } = useApp();

  const [query, setQuery] = useState("");
  const [answer, setAnswer] = useState<SearchAnswer | null>(null);
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);
  const [panel, setPanel] = useState<"person" | "entity" | "date" | null>(null);

  // A reset returns to the account's scope, never to a narrower one — the same rule the
  // desktop follows, and the reason `default_search_scope` exists.
  const unfiltered = useMemo<SearchFilters>(
    () => ({ ...UNFILTERED, scope: settings?.defaultSearchScope ?? "both" }),
    [settings?.defaultSearchScope],
  );

  const [filters, setFilters] = useState<SearchFilters>(unfiltered);

  const run = useCallback(
    async (text: string, next: SearchFilters) => {
      if (!text) return;

      setBusy(true);
      setProblem(null);
      try {
        setAnswer(await api.search(searchRequest(text, next)));
      } catch (cause: unknown) {
        setProblem(cause instanceof ApiError ? cause.message : "That search could not be run.");
      } finally {
        setBusy(false);
      }
    },
    [api],
  );

  const narrow = useCallback(
    (patch: Partial<SearchFilters>) => {
      const next = { ...filters, ...patch };
      setFilters(next);
      setPanel(null);
      if (answer) void run(query.trim(), next);
    },
    [answer, filters, query, run],
  );

  const person = people.find((row) => row.id === filters.personId) ?? null;
  const entity = entities.find((row) => row.id === filters.entityId) ?? null;
  const narrowed = isNarrowed(filters, unfiltered);

  return (
    <div className="flex flex-col gap-3 p-4">
      <input
        type="search"
        value={query}
        onChange={(event) => setQuery(event.target.value)}
        onKeyDown={(event) => {
          if (event.key === "Enter" && !busy) {
            // Dismisses the on-screen keyboard, so the results are not answered into a
            // third of a screen.
            event.currentTarget.blur();
            void run(query.trim(), filters);
          }
        }}
        placeholder="Search your world view…"
        aria-label="Search"
        enterKeyHint="search"
        // 16 px, because iOS Safari zooms into anything smaller and does not zoom back out.
        className="h-11 w-full rounded-[var(--radius-md)] border border-[var(--input)] bg-[var(--surface-glass)] px-3.5 text-[16px] text-[var(--text-primary)] outline-none placeholder:text-[var(--text-muted)]"
      />

      <div className="phone-scroll -mx-4 flex items-center gap-2 overflow-x-auto px-4 pb-0.5">
        <FilterChip
          label={person ? person.name : "Anyone"}
          active={filters.personId !== null}
          onClick={() => setPanel(panel === "person" ? null : "person")}
        />
        <FilterChip
          label={entity ? entity.name : "Any entity"}
          active={filters.entityId !== null}
          onClick={() => setPanel(panel === "entity" ? null : "entity")}
        />
        <FilterChip
          label={dateLabel(filters.from)}
          active={filters.from !== null}
          onClick={() => setPanel(panel === "date" ? null : "date")}
        />

        <span className="inline-flex shrink-0 items-center gap-2 pl-0.5">
          <button
            type="button"
            role="switch"
            aria-checked={filters.currentBeliefsOnly}
            aria-label="Current beliefs only"
            onClick={() => narrow({ currentBeliefsOnly: !filters.currentBeliefsOnly })}
            className={cn(
              "relative h-[18px] w-[34px] shrink-0 rounded-[var(--radius-full)] transition-colors",
              filters.currentBeliefsOnly ? "bg-[var(--primary)]" : "bg-[var(--surface-2)]",
            )}
          >
            <span
              className={cn(
                "absolute top-[3px] size-3 rounded-[var(--radius-full)] bg-[var(--text-inverse)] transition-[left]",
                filters.currentBeliefsOnly ? "left-[19px]" : "left-[3px]",
              )}
            />
          </button>
          <span className="whitespace-nowrap text-[13px] text-[var(--text-secondary)]">Current only</span>
        </span>
      </div>

      {panel === "person" && (
        <Panel>
          <Row label="Anyone" selected={filters.personId === null} onClick={() => narrow({ personId: null })} />
          {people.map((row) => (
            <Row
              key={row.id}
              label={row.name}
              count={row.insightCount}
              selected={filters.personId === row.id}
              onClick={() => narrow({ personId: row.id })}
            />
          ))}
        </Panel>
      )}

      {panel === "entity" && (
        <Panel>
          <Row label="Any entity" selected={filters.entityId === null} onClick={() => narrow({ entityId: null })} />
          {entities.map((row) => (
            <Row
              key={row.id}
              label={row.name}
              count={row.mentionCount}
              selected={filters.entityId === row.id}
              onClick={() => narrow({ entityId: row.id })}
            />
          ))}
        </Panel>
      )}

      {panel === "date" && (
        <Panel>
          {DATE_PRESETS.map((preset) => (
            <Row
              key={preset.id}
              label={preset.label}
              selected={preset.days === null ? filters.from === null : filters.from === daysBack(preset.days)}
              onClick={() =>
                narrow(preset.days === null ? { from: null, to: null } : { from: daysBack(preset.days), to: null })
              }
            />
          ))}
        </Panel>
      )}

      <div
        role="group"
        aria-label="Which altitudes to search"
        className="inline-flex gap-0.5 rounded-[var(--radius-full)] border border-[var(--border-subtle)] bg-[var(--surface-glass)] p-0.5"
      >
        {SEARCH_SCOPES.map((scope) => (
          <button
            key={scope.id}
            type="button"
            aria-pressed={filters.scope === scope.id}
            onClick={() => narrow({ scope: scope.id })}
            className={cn(
              "h-[34px] flex-1 whitespace-nowrap rounded-[var(--radius-full)] text-[13px]",
              filters.scope === scope.id
                ? "bg-[var(--primary)] font-semibold text-[var(--text-inverse)]"
                : "font-medium text-[var(--text-secondary)]",
            )}
          >
            {scope.label}
          </button>
        ))}
      </div>

      {problem && (
        <p role="alert" className="text-[13px] text-[var(--warning)]">
          {problem}
        </p>
      )}

      <p className="text-[12px] text-[var(--text-muted)]">
        {busy
          ? "Searching…"
          : answer
            ? `${answer.results.length === 1 ? "1 result" : `${answer.results.length} results`} · ${SEARCH_MODE_LABEL[answer.mode]}`
            : "Type a question and press Search."}
      </p>

      {!busy && answer && answer.results.length === 0 && (
        <div className="flex flex-col items-center gap-3 border-t border-[var(--border-subtle)] py-7">
          <p className="text-[14px] text-[var(--text-secondary)]">
            {narrowed ? "Nothing matches those filters." : "Nothing matches that."}
          </p>
          {narrowed && (
            <button
              type="button"
              onClick={() => {
                setFilters(unfiltered);
                setPanel(null);
                void run(query.trim(), unfiltered);
              }}
              className="h-11 rounded-[var(--radius-md)] border border-[var(--border-default)] px-[18px] text-[14px] text-[var(--text-primary)]"
            >
              Reset filters
            </button>
          )}
        </div>
      )}

      {!busy && answer && answer.results.length > 0 && (
        <ul className="flex flex-col">
          {answer.results.map((result) => (
            <li key={result.id}>
              <Row2 result={result} />
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function dateLabel(from: string | null): string {
  if (from === null) return "Any time";

  const preset = DATE_PRESETS.find((candidate) => candidate.days !== null && daysBack(candidate.days) === from);
  return preset?.label ?? `Since ${formatDate(from)}`;
}

function FilterChip({ label, active, onClick }: { label: string; active: boolean; onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        "h-9 shrink-0 whitespace-nowrap rounded-[var(--radius-full)] border px-3.5 text-[13px]",
        active
          ? "border-[var(--primary)] bg-[color-mix(in_oklch,var(--primary)_18%,transparent)] text-[var(--text-primary)]"
          : "border-[var(--border-default)] bg-transparent text-[var(--text-secondary)]",
      )}
    >
      {label}
    </button>
  );
}

function Panel({ children }: { children: React.ReactNode }) {
  return (
    <div className="phone-scroll flex max-h-[230px] flex-col gap-px overflow-y-auto rounded-[var(--radius-lg)] border border-[var(--border-default)] bg-[var(--surface-2)] p-1.5">
      {children}
    </div>
  );
}

function Row({
  label,
  count,
  selected,
  onClick,
}: {
  label: string;
  count?: number;
  selected: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        "flex min-h-10 items-center gap-2 rounded-[var(--radius-md)] px-2.5 py-2 text-left text-[14px]",
        selected ? "bg-[var(--sidebar-accent)] font-semibold text-[var(--text-primary)]" : "text-[var(--text-secondary)]",
      )}
    >
      <span className="min-w-0 flex-1 truncate">{label}</span>
      {count !== undefined && <span className="text-[12px] text-[var(--text-muted)]">{count}</span>}
    </button>
  );
}

/**
 * One result. The altitude is on it in words, not only in a colour — SRCH-3's whole point is
 * that the reader always knows whether they are looking at something one person said once,
 * or at what the world view now holds.
 */
function Row2({ result }: { result: SearchResult }) {
  const insight = result.altitude === "insight";
  const Icon = insight ? Lightbulb : BookOpenText;

  const href = insight
    ? result.sourceMaterialId
      ? `${result.sourceKind === "recording" ? "/recordings" : "/sources"}/${result.sourceMaterialId}?insight=${result.insightId}`
      : null
    : result.subjectSlug
      ? `/world-view/${result.subjectSlug}`
      : null;

  const meta = insight
    ? ["Insight", result.sourceTitle, formatDate(result.sourceDate)].filter(Boolean).join(" · ")
    : ["World view", result.subjectKind ? subjectKindMeta(result.subjectKind).label : null, result.subjectTitle]
        .filter(Boolean)
        .join(" › ");

  const row = (
    <div className="flex flex-col gap-1.5 border-t border-[var(--border-subtle)] py-3.5">
      <div className="flex items-baseline gap-2">
        <Icon
          size={13}
          strokeWidth={2}
          aria-hidden
          className={cn("shrink-0", insight ? "text-[var(--vl-virtual)]" : "text-[var(--vl-life)]")}
        />
        <span className="min-w-0 flex-1 text-[15px] font-semibold text-[var(--vl-bridge)]">{result.title}</span>
        <span
          className="whitespace-nowrap text-[11px] font-semibold"
          style={{ color: insight ? "var(--vl-virtual)" : "var(--vl-life)" }}
        >
          {insight ? "Insight" : "World view"}
        </span>
      </div>

      {result.snippet && (
        <p className="text-[14px] leading-[1.45] text-[var(--text-secondary)] [text-wrap:pretty]">
          {result.snippet}
        </p>
      )}

      <p className="truncate text-[11px] text-[var(--text-muted)]">{meta}</p>
    </div>
  );

  return href ? (
    <Link to={href} className="block">
      {row}
    </Link>
  ) : (
    row
  );
}
